import { useMemo } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { AxiosResponse } from 'axios';
import retry from 'async-retry';
import Head from 'next/head';

import { getLayout } from 'components/layouts/Header';
import { OpportunityPage } from 'components/opportunity-page';
import SearchResults from 'pages/search/results/index.page';
import { API_ROUTES } from 'lib/api-routes';
import { asyncRetryParams } from 'lib/consts';
import { EventTouchpointable } from 'lib/models/event';
import {
  TOpportunity,
  OpportunityResponse,
  TouchpointState,
} from 'lib/models/opportunity';
import { SearchPageResponse } from 'lib/models/search-page';
import { TouchpointType } from 'lib/models/touchpoint';
import { pageBuilderInstance } from 'lib/utils/axios';
import { parseResponse } from 'lib/utils/parser';
import { LOCALE_PAGES, fetchTranslationFiles } from 'lib/services/page';
import { PAGE_ROUTES } from 'lib/page-routes';
import { getSearchPageTypeFromTouchpointType } from 'lib/utils/global.search';
import { getEventState } from 'utils/format';
import { isBefore1OCT2023 } from 'utils/time';
import { TQueryParams } from 'utils/search';

export type SearchPageProps = {
  title: string;
  metaTitle: string;
  page_path: string;
  opportunities: Array<TOpportunity>;
  touchpointable_type: TouchpointType;
  queryParams: TQueryParams;
  opportunity: TOpportunity;
  canonicalURL: string;
};

const GlobalSearchPage: React.FC<SearchPageProps> = ({
  title,
  metaTitle,
  touchpointable_type,
  queryParams,
  page_path,
  opportunity,
  canonicalURL,
}) => {
  if (opportunity) {
    return (
      <OpportunityPage opportunity={opportunity} canonicalURL={canonicalURL} />
    );
  }

  const jobOrInternshipDescription = `Apply to ${title} on Huzzle, the top careers network for students.`;
  const eventMetaDescription = `Find the top ${title} on Huzzle, register in minutes.`;
  const metaDescription =
    touchpointable_type === TouchpointType.Event
      ? eventMetaDescription
      : jobOrInternshipDescription;
  const canonicalURLSearchPage = `${process.env.NEXT_PUBLIC_SITE_URL}/${page_path}`;
  const pageType = getSearchPageTypeFromTouchpointType(touchpointable_type);

  const pageHead = useMemo(() => {
    return (
      <Head>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalURLSearchPage} />
      </Head>
    );
  }, [metaTitle, metaDescription]);

  return (
    <SearchResults
      requestedPageType={pageType}
      pageHead={pageHead}
      pageTitle={title}
      queryParams={queryParams}
    />
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const url = `/api/v1/search_pages?per_page=1000000000`;
  const eventUrl = `/api/v1/touchpoints/basic_index?touchpointable_types=Event&only_short_slug=true&per_page=1000000000`;
  const { data } = await pageBuilderInstance.get<Array<string>>(url);
  const { data: eventData } = await pageBuilderInstance.get<Array<string>>(
    eventUrl
  );
  data.push(...eventData);

  const paths = data.map((url_path) => {
    return {
      params: { searchPageSlug: url_path },
    };
  });

  return {
    paths,
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const searchPageSlug = params?.searchPageSlug as string;
  let propsData;

  const eventsShortlinkList = [
    'h43muo8kzi',
    'mw2li1eu7r',
    'ecrzm2f9xz',
    'nicrxa4kwt',
    'uqw3ept7hx',
    '4hyxicfqf3',
    'cutkj5fef2',
    'i2l4kscnbu',
    'm4awjlmv3h',
    '7bnloc5l9a',
    'kz2vbuw2j1',
    'qmjdelxlep',
    'c0bftbsiqp',
  ];
  //check here if event page
  //all events short slug start with e4 and is 8 characters long
  // hardcoding events which have been wrongly generated without e4 and 8 letters
  // there are 13 such live events
  if (
    (searchPageSlug.length == 8 && searchPageSlug.startsWith('e4')) ||
    eventsShortlinkList.includes(searchPageSlug)
  ) {
    const pathname = `${API_ROUTES.CAMPAIGNS}/${searchPageSlug}`;

    try {
      propsData = await retry(async (bail) => {
        const { data } = await pageBuilderInstance<OpportunityResponse>(
          pathname
        );
        const opportunity = parseResponse(data);
        const { touchpointable_type } = opportunity;

        if (touchpointable_type !== 'Event') {
          bail(new Error('Touchpoint is not a Event'));
          throw new Error('Touchpoint is not a Event');
        }

        const { touchpointable, application_deadline, slug } = opportunity;
        const { start_date, end_date } = parseResponse(
          touchpointable as EventTouchpointable
        );
        const eventState = getEventState(
          start_date,
          end_date,
          application_deadline,
          false
        );
        const isExpired =
          eventState === TouchpointState.CLOSED ||
          eventState === TouchpointState.ENDED;
        const isExpiredBeforeDeadline =
          isExpired && isBefore1OCT2023(end_date || start_date);

        const canonicalURL = `${process.env.NEXT_PUBLIC_SITE_URL}/events/${slug}`;
        return { opportunity, canonicalURL, isExpiredBeforeDeadline };
      }, asyncRetryParams);
    } catch (error) {
      console.error(`Event page not found - ${searchPageSlug}: ${error}`);
      return { notFound: true };
    }

    const { isExpiredBeforeDeadline, ...props } = propsData;

    propsData = props;

    if (isExpiredBeforeDeadline) {
      return {
        redirect: {
          destination: PAGE_ROUTES.EVENTS_SEARCH,
          permanent: true,
        },
      };
    }
  } else {
    const url = `/api/v2/search_pages/${searchPageSlug}`;

    try {
      propsData = await retry(async () => {
        const { data } = await pageBuilderInstance.get<
          AxiosResponse<SearchPageResponse>
        >(url);
        const { title, query_params, url_path } = parseResponse(data);
        const year = new Date().getFullYear();
        const titleWithYear = `${title} - ${year}`;
        const effectiveTitle =
          titleWithYear.length > 55 ? title : titleWithYear;

        const {
          touchpointable_types = [],
          area_of_responsibility_ids = [],
          topic_ids = [],
          event_category_ids = [],
          industry_ids = [],
          country_ids = [],
          city_ids = [],
          kinds = [],
          remote_country_ids = [],
        } = query_params;
        const [touchpointable_type] =
          touchpointable_types as Array<TouchpointType>;
        const type = getSearchPageTypeFromTouchpointType(touchpointable_type);
        const redirectURLParams = {
          type: type,
          area_of_responsibility_ids: area_of_responsibility_ids,
          topic_ids: topic_ids,
          event_category_ids: event_category_ids,
          industry_ids: industry_ids,
          country_ids: country_ids || remote_country_ids,
          city_ids: city_ids,
          event_type: kinds,
        };

        return {
          metaTitle: effectiveTitle,
          title,
          page_path: url_path,
          touchpointable_type,
          queryParams: redirectURLParams,
          opportunity: null,
        };
      }, asyncRetryParams);
    } catch (error) {
      console.error(error);
      return { notFound: true };
    }
  }

  return {
    props: {
      ...propsData,
      ...(await fetchTranslationFiles({
        locale,
        pages: propsData.opportunity
          ? [LOCALE_PAGES.OPPORTUNITY]
          : [LOCALE_PAGES.SEARCH_RESULTS, LOCALE_PAGES.OPPORTUNITY],
      })),
    },
    revalidate: 3600,
  };
};

// @ts-ignore
GlobalSearchPage.getLayout = getLayout;

export default GlobalSearchPage;
